/**
 * DispositionsReport — the recruiter outcome matrix (Reports → Dispositions).
 *
 * Rows = recruiters, columns = the named outcomes (positive milestones + the four
 * negative dispositions). Each cell = the DISTINCT leads that recruiter moved into
 * that status in the range, attributed to the owner frozen on the stage-events
 * ledger (so every code path counts). Clicking a NEGATIVE cell (or its column
 * header for the whole team) drills into WHY: the reason-code breakdown, then the
 * individual leads behind a reason. No chart library — CSS bars + a heat table.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../../services/api';
import { DispositionAnalytics, DispositionReasonRow, DispositionContact, contactName } from '../../types';
import { lifecycleMeta, dispositionReasonLabel, relTime } from '../../lib';
import BarRow from './BarRow';
import { SummaryStat } from './StatBlocks';
import { Z } from '../ui';

interface Props {
  from: string | null;
  to: string | null;
  studyId?: string | null;
}

const POSITIVE = ['qualified_pending_records', 'ready_to_schedule', 'scheduled', 'screened', 'randomized'];
const NEGATIVE = ['screen_failed', 'not_eligible', 'not_interested', 'unreachable'];

// Compact column headers (the full lifecycle labels are too wide for a matrix);
// the full label rides the title tooltip.
const SHORT_LABEL: Record<string, string> = {
  qualified_pending_records: 'Qualified',
  ready_to_schedule: 'Ready to sched.',
  scheduled: 'Scheduled',
  screened: 'Screened',
  randomized: 'Randomized',
  screen_failed: 'Screen failed',
  not_eligible: 'Not eligible',
  not_interested: 'Not interested',
  unreachable: 'Unreachable',
};

/** Heat shade for a count, by value / column-max — cyan for positives, rose for negatives. */
function heatClass(value: number, max: number, negative: boolean): string {
  if (!value || max <= 0) return '';
  const r = value / max;
  if (negative) {
    if (r > 0.66) return 'bg-rose-500 text-white';
    if (r > 0.33) return 'bg-rose-300 text-rose-900 dark:bg-rose-800 dark:text-rose-100';
    return 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200';
  }
  if (r > 0.66) return 'bg-cyan-500 text-white';
  if (r > 0.33) return 'bg-cyan-300 text-cyan-900 dark:bg-cyan-800 dark:text-cyan-100';
  return 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-200';
}

interface Drill { status: string; recruiter: string | null; label: string; }

export default function DispositionsReport({ from, to, studyId = null }: Props) {
  const [data, setData] = useState<DispositionAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [drill, setDrill] = useState<Drill | null>(null);
  const [reasonRows, setReasonRows] = useState<DispositionReasonRow[] | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  const [leads, setLeads] = useState<DispositionContact[] | null>(null);

  // Matrix.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setDrill(null);
    api.get('/api/crm/analytics/dispositions', {
      params: { from: from || undefined, to: to || undefined, study_id: studyId || undefined },
    })
      .then((res) => { if (alive) { setData(res.data || null); setError(null); } })
      .catch((err) => { if (alive) setError(err?.response?.data?.error || 'Failed to load dispositions'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [from, to, studyId]);

  // Reason breakdown for the drilled cell / column.
  useEffect(() => {
    if (!drill) { setReasonRows(null); setReason(null); setLeads(null); return; }
    let alive = true;
    setReasonRows(null);
    setReason(null);
    setLeads(null);
    api.get('/api/crm/analytics/dispositions/reasons', {
      params: {
        to_status: drill.status,
        recruiter: drill.recruiter || undefined,
        from: from || undefined,
        to: to || undefined,
        study_id: studyId || undefined,
      },
    })
      .then((res) => { if (alive) setReasonRows(res.data?.rows || []); })
      .catch(() => { if (alive) setReasonRows([]); });
    return () => { alive = false; };
  }, [drill, from, to, studyId]);

  // Leads behind the selected reason.
  useEffect(() => {
    if (!drill || !reason) { setLeads(null); return; }
    let alive = true;
    setLeads(null);
    api.get('/api/crm/analytics/dispositions/reason-contacts', {
      params: {
        to_status: drill.status,
        reason_code: reason,
        recruiter: drill.recruiter || undefined,
        from: from || undefined,
        to: to || undefined,
        study_id: studyId || undefined,
        limit: 50,
      },
    })
      .then((res) => { if (alive) setLeads(res.data?.contacts || []); })
      .catch(() => { if (alive) setLeads([]); });
    return () => { alive = false; };
  }, [drill, reason, from, to, studyId]);

  const statuses = data?.statuses || [...POSITIVE, ...NEGATIVE];
  const negatives = useMemo(() => new Set(data?.negative_statuses || NEGATIVE), [data]);
  const recruiters = useMemo(
    () => (data?.recruiters || []).filter((r) => r.total > 0),
    [data],
  );
  // Per-column max for heat shading.
  const colMax = useMemo(() => {
    const m: Record<string, number> = {};
    for (const s of statuses) m[s] = Math.max(1, ...recruiters.map((r) => r.counts[s] || 0));
    return m;
  }, [statuses, recruiters]);

  // The count behind the active drill (a cell, or a whole column when recruiter
  // is null) — used to surface the "no reason recorded" remainder honestly.
  const drillCount = useMemo(() => {
    if (!drill || !data) return 0;
    if (drill.recruiter) {
      return data.recruiters.find((r) => r.user_id === drill.recruiter)?.counts[drill.status] || 0;
    }
    return data.totals[drill.status] || 0;
  }, [drill, data]);

  const reasonMax = useMemo(() => Math.max(1, ...(reasonRows || []).map((r) => Number(r.total))), [reasonRows]);
  const reasonSum = useMemo(() => (reasonRows || []).reduce((s, r) => s + Number(r.total), 0), [reasonRows]);
  const unspecified = Math.max(0, drillCount - reasonSum);

  if (loading) return <p className="p-4 text-sm text-gray-400">Loading dispositions…</p>;
  if (error) return <p className="p-4 text-sm text-red-600 dark:text-red-400">{error}</p>;
  if (!data || recruiters.length === 0) {
    return <p className="p-4 text-sm text-gray-500 dark:text-gray-400">No leads were dispositioned in this range yet.</p>;
  }

  const t = data.totals;
  const cell = (status: string, count: number, recruiter: string | null, recruiterName: string) => {
    const neg = negatives.has(status);
    const isActive = drill?.status === status && drill?.recruiter === recruiter;
    const content = count > 0 ? (
      <span className={`inline-block min-w-[1.75rem] rounded px-1.5 py-0.5 tabular-nums ${heatClass(count, colMax[status], neg)} ${isActive ? 'ring-2 ring-offset-1 ring-primary-500 dark:ring-offset-gray-800' : ''}`}>
        {count.toLocaleString()}
      </span>
    ) : (
      <span className="text-gray-300 dark:text-gray-600">·</span>
    );
    if (neg && count > 0) {
      return (
        <button
          type="button"
          onClick={() => setDrill((d) => (d?.status === status && d?.recruiter === recruiter
            ? null
            : { status, recruiter, label: `${recruiterName} · ${lifecycleMeta(status).label}` }))}
          className="w-full"
          title={`Why ${recruiterName}'s leads went ${lifecycleMeta(status).label.toLowerCase()}`}
        >
          {content}
        </button>
      );
    }
    return content;
  };

  return (
    <div className="space-y-4">
      {/* Network negative-disposition totals */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
        {NEGATIVE.map((s) => (
          <SummaryStat key={s} label={lifecycleMeta(s).label} value={(t[s] || 0).toLocaleString()} />
        ))}
        <SummaryStat label="Randomized" value={(t.randomized || 0).toLocaleString()} highlight={(t.randomized || 0) > 0} />
      </div>

      {data.study_scoped && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
          Scoped to the selected study (by each lead's study at the time it moved).
        </p>
      )}

      {/* The recruiter × disposition matrix */}
      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="border-b border-gray-100 dark:border-gray-700">
              <th rowSpan={2} className={`sticky left-0 ${Z.rail} bg-white px-3 py-2 text-left align-bottom font-medium text-gray-500 dark:bg-gray-800 dark:text-gray-400`}>
                Recruiter
              </th>
              <th colSpan={POSITIVE.length} className="px-2 py-1.5 text-center font-medium text-cyan-700 dark:text-cyan-300">
                Positive milestones
              </th>
              <th colSpan={NEGATIVE.length} className="px-2 py-1.5 text-center font-medium text-rose-700 dark:text-rose-300">
                Negative dispositions
              </th>
              <th rowSpan={2} className="px-3 py-2 text-right align-bottom font-medium text-gray-500 dark:text-gray-400">Total</th>
            </tr>
            <tr className="border-b border-gray-100 dark:border-gray-700">
              {statuses.map((s) => {
                const neg = negatives.has(s);
                const headBtn = neg ? (
                  <button
                    type="button"
                    onClick={() => setDrill((d) => (d?.status === s && d?.recruiter === null
                      ? null
                      : { status: s, recruiter: null, label: `All recruiters · ${lifecycleMeta(s).label}` }))}
                    className={`hover:underline ${drill?.status === s && drill?.recruiter === null ? 'text-primary-700 underline dark:text-primary-300' : ''}`}
                    title={`Reasons across the team — ${lifecycleMeta(s).label}`}
                  >
                    {SHORT_LABEL[s] || s}
                  </button>
                ) : (SHORT_LABEL[s] || s);
                return (
                  <th
                    key={s}
                    className={`px-2 py-2 text-center font-medium ${neg ? 'text-rose-600 dark:text-rose-300' : 'text-gray-400'}`}
                    title={lifecycleMeta(s).label}
                  >
                    {headBtn}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {recruiters.map((r) => (
              <tr key={r.user_id} className="border-b border-gray-50 last:border-0 dark:border-gray-700/50">
                <td className={`sticky left-0 ${Z.rail} max-w-[10rem] truncate bg-white px-3 py-1.5 font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-200`}>
                  {r.name || 'Unknown'}
                </td>
                {statuses.map((s) => (
                  <td key={s} className="px-1 py-1 text-center">
                    {cell(s, r.counts[s] || 0, r.user_id, r.name || 'Unknown')}
                  </td>
                ))}
                <td className="px-3 py-1.5 text-right font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                  {r.total.toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-gray-200 dark:border-gray-600">
              <td className={`sticky left-0 ${Z.rail} bg-white px-3 py-1.5 font-semibold text-gray-700 dark:bg-gray-800 dark:text-gray-200`}>Team</td>
              {statuses.map((s) => (
                <td key={s} className="px-1 py-1.5 text-center font-semibold tabular-nums text-gray-700 dark:text-gray-200">
                  {(t[s] || 0).toLocaleString()}
                </td>
              ))}
              <td className="px-3 py-1.5 text-right font-bold tabular-nums text-gray-900 dark:text-gray-100">
                {data.grand_total.toLocaleString()}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <p className="px-1 text-[11px] leading-snug text-gray-400">
        Each cell counts the distinct leads a recruiter moved into that status, credited to whoever owned the lead at the
        time of the move — including outcomes CRIO pushed back (a CRIO screen fail is credited to the lead's owner, tagged
        <span className="mx-1 inline-block rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-300">CRIO</span>
        in the reason drill). Leads with no recruiter owner (e.g. created directly in CRIO) roll up under <span className="font-medium">Unassigned</span>.
        Click a <span className="text-rose-500">negative</span> cell (or its column header) to see the reasons.
      </p>

      {/* Reason drill-down for the selected cell / column */}
      {drill && (
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{drill.label}</h3>
            <button type="button" onClick={() => setDrill(null)} className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
              Close
            </button>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            {/* Reasons */}
            <div>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Reasons</h4>
              {reasonRows === null ? (
                <p className="text-sm text-gray-400">Loading…</p>
              ) : reasonRows.length === 0 && unspecified === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">No recorded reasons.</p>
              ) : (
                <div className="space-y-0.5">
                  {reasonRows.map((row) => (
                    <BarRow
                      key={row.reason_code}
                      label={dispositionReasonLabel(drill.status, row.reason_code)}
                      count={Number(row.total)}
                      max={reasonMax}
                      color="bg-rose-400"
                      active={reason === row.reason_code}
                      onClick={() => setReason((p) => (p === row.reason_code ? null : row.reason_code))}
                    />
                  ))}
                  {unspecified > 0 && (
                    <BarRow
                      label="No reason recorded"
                      count={unspecified}
                      max={reasonMax}
                      color="bg-gray-300 dark:bg-gray-600"
                    />
                  )}
                </div>
              )}
            </div>

            {/* Leads behind the selected reason */}
            <div>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                {reason ? dispositionReasonLabel(drill.status, reason) : 'Leads'}
              </h4>
              {!reason ? (
                <p className="text-sm text-gray-400 dark:text-gray-500">Click a reason to see the leads behind it.</p>
              ) : leads === null ? (
                <p className="text-sm text-gray-400">Loading…</p>
              ) : leads.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">No leads.</p>
              ) : (
                <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                  {leads.map((d) => (
                    <li key={d.contact_id} className="py-2">
                      <div className="flex items-center justify-between gap-2">
                        {d.contact ? (
                          <Link
                            to={`/crm/contacts/${d.contact_id}`}
                            className="min-w-0 truncate text-sm font-medium text-primary-700 hover:text-primary-800 dark:text-primary-400"
                          >
                            {contactName(d.contact)}
                          </Link>
                        ) : (
                          <span className="text-sm text-gray-500">(contact)</span>
                        )}
                        <span className="flex-shrink-0 text-xs text-gray-400">{relTime(d.occurred_at)} ago</span>
                      </div>
                      {d.reason_note && (
                        <p className="mt-0.5 text-xs italic text-gray-500 dark:text-gray-400">"{d.reason_note}"</p>
                      )}
                      {d.source && d.source !== 'recruiter' && (
                        <span className="mt-0.5 inline-block rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-300">
                          {d.source === 'automation' ? 'Auto'
                            : d.source === 'disqualification' ? 'Prescreen'
                            : d.source === 'ctms' ? 'CRIO'
                            : d.source}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
